import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api, apiErrorMessage } from "@/lib/api";
import AIConciergeDrawer from "@/components/AIConciergeDrawer";
import { FileText, BookOpen, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

/**
 * CC&R / Document Assistant — chat against the community's uploaded
 * governing documents.  The backend `document_assistant` agent auto-injects
 * a "Documents on file" block into the system prompt so the assistant can
 * cite real filenames instead of making up clauses.
 *
 * On this page we ALSO surface the doc list so the resident sees what the
 * assistant can actually reference before they ask.
 */
export default function DocumentAssistantPage() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/documents");
        setDocs(data.items || data || []);
      } catch (e) { toast.error(apiErrorMessage(e)); }
      setLoading(false);
    })();
  }, []);

  const cardBg = isAdmin ? "border-white/10 bg-card" : "bg-white";
  const softCol = isAdmin ? "text-zinc-400" : "text-zinc-500";
  const textCol = isAdmin ? "text-white" : "text-zinc-900";

  return (
    <div>
      <PageHeader
        eyebrow="AI · Documents"
        title="CC&R / Document Assistant"
        subtitle="Ask questions about the CC&Rs, bylaws, and rules. I cite what's actually on file."
      />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Docs sidebar */}
        <Card className={cardBg + " lg:col-span-2 p-5"} data-testid="doc-catalog">
          <div className="overline flex items-center gap-1">
            <BookOpen className="h-3 w-3" /> On file ({docs.length})
          </div>
          <div className="text-xs mt-1 mb-3 leading-relaxed" style={{ color: isAdmin ? "#a1a1aa" : "#71717a" }}>
            The assistant has access to these documents. Ask about any rule and it&apos;ll
            reference the specific one it&apos;s pulling from.
          </div>
          {loading ? (
            <div className={softCol + " text-xs"}>Loading community documents…</div>
          ) : docs.length === 0 ? (
            <div className={
              "text-xs rounded-md p-3 " +
              (isAdmin ? "bg-white/[0.03] border border-white/10 text-amber-300"
                        : "bg-amber-50 border border-amber-200 text-amber-800")
            }>
              No documents uploaded yet. Ask the property manager to upload the CC&amp;Rs so I can
              answer with authority.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[52vh] overflow-y-auto pr-1">
              {docs.map((d) => (
                <a
                  key={d.id}
                  href={d.url || d.file_url || "#"}
                  target={d.url || d.file_url ? "_blank" : undefined}
                  rel="noreferrer"
                  className={
                    "flex items-start gap-2 rounded-md px-2.5 py-2 border transition text-xs " +
                    (isAdmin
                      ? "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                      : "border-zinc-200 hover:bg-zinc-50")
                  }
                  data-testid={`doc-${d.id}`}
                >
                  <FileText className={"h-3.5 w-3.5 mt-0.5 shrink-0 " + (isAdmin ? "text-emerald-300" : "text-emerald-600")} />
                  <div className="flex-1 min-w-0">
                    <div className={"font-medium truncate " + textCol}>{d.name || d.title || "Untitled"}</div>
                    <div className={softCol + " text-[10px]"}>
                      {d.category || "General"}
                      {d.updated_at ? ` · ${new Date(d.updated_at).toLocaleDateString()}` : ""}
                    </div>
                  </div>
                  {(d.url || d.file_url) && <ArrowUpRight className={"h-3 w-3 " + softCol} />}
                  {(!d.url && !d.file_url) && (
                    <Badge variant="outline" className={isAdmin ? "border-white/10 text-zinc-400 text-[9px] px-1.5 py-0" : "border-zinc-200 text-zinc-500 text-[9px] px-1.5 py-0"}>
                      indexed
                    </Badge>
                  )}
                </a>
              ))}
            </div>
          )}
        </Card>

        {/* Chat */}
        <Card className={cardBg + " lg:col-span-3 overflow-hidden h-[70vh] flex flex-col"}>
          <div className="flex-1 min-h-0">
            <AIConciergeDrawer agent="document_assistant" theme={isAdmin ? "dark" : "light"} />
          </div>
        </Card>
      </div>
    </div>
  );
}
